import { useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { ExternalLink, Info, ZoomIn } from "lucide-react";
import { useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { Can } from "@/components/can";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "@/components/ui/dialog";
import { queryKeys } from "@/lib/query-keys";

import type { ClaimWithRelations } from "../../api/use-claim";
import { CancelledClaimBanner } from "../banners";
import { FlightOverview } from "../flight-overview";
import { PassengerTable } from "../passengers/passenger-table";
import { AGENT_FEEDBACK_STEPS, getClaimOverview, getInitialStep } from "./agent-feedback-steps";
import { AdditionalLossRequestAlert, FormAdditionalLoss } from "./form-additional-loss";
import { FormEligibilityVerification } from "./form-eligibility-verification";
import { FormUploadDocuments } from "./form-upload-documents";
import { StepsBullet } from "./steps-bullet";

/** Port of pages/plugin/AgentFeedback.vue — manual (agent-mediated) passenger-contact workflow. */
export function AgentFeedbackFlow({ claim }: { claim: ClaimWithRelations }) {
  const queryClient = useQueryClient();

  const [currentStep, setCurrentStep] = useState(() =>
    getInitialStep({ agentFeedback: claim.agentFeedback, claimStateType: claim.claimState.type }),
  );
  const [reservationOpen, setReservationOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(true);

  const claimOverview = getClaimOverview(claim);

  async function nextStep(increment = 1) {
    setCurrentStep((step) => step + increment);
    await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claim.id) });
  }

  if (claim.claimLegalStep.name === "CANCELLED") {
    return showBanner ? <CancelledClaimBanner onClose={() => setShowBanner(false)} /> : null;
  }

  if (claim.claimState.legalStepId !== 0) {
    return (
      <div>
        <div className="pointer-events-none bg-stone-50 py-16 sm:flex sm:justify-center sm:px-6 lg:px-8">
          <div className="pointer-events-auto flex cursor-help items-center justify-between gap-x-6 border-2 border-stone-600 bg-stone-700 px-6 py-2.5 shadow-xl sm:rounded-xl sm:py-3 sm:pr-3.5 sm:pl-4">
            <p className="text-sm leading-6 text-white">
              <span className="inline-flex items-center">
                <Info className="mr-3 h-5 w-5" />
                <strong className="font-semibold">
                  Sur ce dossier : Flux de contact manuel (via l'agence)
                </strong>
              </span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  const step = AGENT_FEEDBACK_STEPS[currentStep];

  return (
    <div>
      {/* Port of the "Visualiser la demande" AlertModal (Overview + PassengerTable) */}
      <Dialog open={reservationOpen} onOpenChange={setReservationOpen}>
        <DialogPopup className="max-w-7xl">
          <DialogHeader>
            <DialogTitle>Visualiser la demande</DialogTitle>
            <p className="text-sm text-gray-500">La réservation et le segment détecté par Upclaim</p>
          </DialogHeader>
          <DialogPanel>
            <div className="flex w-full flex-col gap-6 xl:flex-row xl:divide-x-2">
              <div className="xl:w-1/2 xl:px-6">
                <FlightOverview
                  claimId={claim.id}
                  airline={claim.legalSpecification.effectiveAirline}
                  claimTypeName={claim.claimType.name}
                  flight={claim.legalSpecification.effectiveFlight}
                  reroutingProposals={claim.legalSpecification.impactedSegment.reroutingProposals}
                  actualRerouting={claim.legalSpecification.impactedSegment.actualRerouting}
                />
              </div>
              <div className="xl:w-1/2">
                <PassengerTable claim={claim} />
              </div>
            </div>
          </DialogPanel>
          <DialogFooter>
            <Button onClick={() => setReservationOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogPopup>
      </Dialog>

      {currentStep === 0 ? (
        <Alert variant="default">
          <Info />
          <AlertTitle className="flex items-baseline justify-between gap-2">
            <span>Feedback demandé ({claimOverview.pnrId})</span>
            {claimOverview.stateUpdatedAt ? (
              <span className="text-xs font-normal text-gray-500">
                Il y a {formatDistanceToNow(claimOverview.stateUpdatedAt, { locale: fr })}
              </span>
            ) : null}
          </AlertTitle>
          <AlertDescription>Nous avons besoin de votre confirmation sur ce dossier</AlertDescription>
        </Alert>
      ) : null}

      {currentStep === 1 ? <AdditionalLossRequestAlert claim={claim} claimOverview={claimOverview} /> : null}

      <div className="bg-gray-50 px-4 pt-2 pb-6 text-left">
        <Can permissions={UPCLAIM_ROLES}>
          <div className="flex justify-center py-2">
            <span className="isolate inline-flex rounded-md shadow-sm">
              <button
                type="button"
                onClick={() => setCurrentStep((step) => (step > 0 ? step - 1 : step))}
                className="relative inline-flex items-center rounded-l-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-gray-300 ring-inset hover:bg-gray-50 focus:z-10"
              >
                Précédent
              </button>
              <button
                type="button"
                onClick={() =>
                  setCurrentStep((step) => (step < AGENT_FEEDBACK_STEPS.length - 1 ? step + 1 : step))
                }
                className="relative -ml-px inline-flex items-center rounded-r-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-gray-300 ring-inset hover:bg-gray-50 focus:z-10"
              >
                Suivant
              </button>
              {claimOverview.feedbackId ? (
                <a
                  href={`https://app.forestadmin.com/Upclaim/Production/Upclaim/data/claimsAgentsFeedbacks/index/record/claimsAgentsFeedbacks/${claimOverview.feedbackId}/details`}
                  target="_blank"
                  rel="noreferrer"
                  className="relative ml-4 inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-gray-300 ring-inset hover:bg-gray-50 focus:z-10"
                >
                  <ExternalLink className="-m-0.5 mr-1 h-5 w-5 text-gray-500" />
                  Ouvrir
                </a>
              ) : null}
            </span>
          </div>
        </Can>

        {/* Steps */}
        <div className="rounded-lg border bg-white py-5 text-gray-700 shadow">
          <StepsBullet steps={AGENT_FEEDBACK_STEPS} currentStep={currentStep} />
        </div>

        <div className="flex w-full content-center border-b py-8">
          <div className="mx-auto">
            <Button size="lg" variant="outline" onClick={() => setReservationOpen(true)}>
              <ZoomIn className="mr-2 h-5 w-5" />
              Voir le dossier complet
            </Button>
          </div>
        </div>

        {/* Form */}
        <div className="rounded-md border bg-white px-6 py-8 text-left shadow">
          {step.stepNumber === 0 ? (
            <FormEligibilityVerification
              claim={claim}
              claimOverview={claimOverview}
              onNextStep={(increment) => void nextStep(increment)}
            />
          ) : step.stepNumber === 1 ? (
            <FormAdditionalLoss
              claim={claim}
              claimOverview={claimOverview}
              onNextStep={(increment) => void nextStep(increment)}
            />
          ) : (
            <FormUploadDocuments claim={claim} claimOverview={claimOverview} />
          )}
        </div>
      </div>
    </div>
  );
}
